import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AnalysisDriverType, Segment, SegmentationGroup, SegmentationMethod } from "../types/types";
import type { SelectChangeEvent } from "@mui/material";
import { Percentile } from "../../../../graphql/types/graphql";
import { context as CubeContext } from "utils/Provider";

interface HeadroomContextType {
  tableName: string | null;
  setTableName: (tableName: string) => void;
  segmentGroups: SegmentationGroup[];
  setSegmentGroups: (segmentGroups: SegmentationGroup[]) => void;
  addSegmentGroup: () => void;
  removeSegmentGroup: (index: number) => void;
  updateSegmentGroupType: (index: number, type: string) => void;
  updateSegmentGroupMethod: (index: number, method: SegmentationMethod) => void;
  updateSegmentGroupSegments: (index: number, segments: Segment[]) => void;
  updateSegmentGroupRangeColumn: (index: number, rangeColumn: string) => void;
  selectedProject: string | undefined;
  setSelectedProject: (project: string) => void;
  driver1Type: string;
  setDriver1Type: (type: string) => void;
  driver2Type: string;
  setDriver2Type: (type: string) => void;
  driver1NumeratorType: string;
  setDriver1NumeratorType: (type: string) => void;
  driver2NumeratorType: string;
  setDriver2NumeratorType: (type: string) => void;
  driver1Mode: AnalysisDriverType;
  setDriver1Mode: (mode: AnalysisDriverType) => void;
  driver2Mode: AnalysisDriverType;
  setDriver2Mode: (mode: AnalysisDriverType) => void;
  handleDriver1Change: (event: SelectChangeEvent<string>) => void;
  handleDriver2Change: (event: SelectChangeEvent<string>) => void;
  handleDriver1NumeratorChange: (event: SelectChangeEvent<string>) => void;
  handleDriver2NumeratorChange: (event: SelectChangeEvent<string>) => void;
  handleDriver1ModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDriver2ModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  count1Column: string;
  setCount1Column: (type: string) => void;
  count2Column: string | null;
  setCount2Column: (type: string) => void;
  shouldSkipSegmentationGroupedCountQuery: boolean;
  setShouldSkipSegmentationGroupedCountQuery: (type: boolean) => void;
  customerColumn?: string;
  setCustomerColumn?: (column: string) => void;
  shouldRefetchDriverMetrics: boolean;
  setShouldRefetchDriverMetrics: (shouldRefetch: boolean) => void;
  driver1Percentile: Percentile;
  setDriver1Percentile: (percentile: Percentile) => void;
  driver2Percentile: Percentile;
  setDriver2Percentile: (percentile: Percentile) => void;
  headroomDriver: "driver1" | "driver2" | null;
  setHeadroomDriver: (driver: "driver1" | "driver2") => void;
  projectNameInput: string;
  setProjectNameInput: (name: string) => void;
  shouldRefetchHeadroomData: boolean;
  setShouldRefetchHeadroomData: (shouldRefetch: boolean) => void;
  headroomDriverPercentile: number;
  setHeadroomDriverPercentile: (percentile: number) => void;
}

const HeadroomContext = createContext<HeadroomContextType | undefined>(undefined);

interface HeadroomProviderProps {
  children: React.ReactNode;
}

export const HeadroomProvider: React.FC<HeadroomProviderProps> = ({ children }) => {
  const { selectedCube } = useContext(CubeContext);
  const [segmentGroups, setSegmentGroups] = useState<SegmentationGroup[]>([
    { index: 0, type: "", method: "DATA", segments: [], rangeColumn: "" },
  ]);
  const [tableName, setTableName] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [driver1Type, setDriver1Type] = useState<string>("");
  const [driver2Type, setDriver2Type] = useState<string>("");
  const [driver1NumeratorType, setDriver1NumeratorType] = useState<string>("");
  const [driver2NumeratorType, setDriver2NumeratorType] = useState<string>("");
  const [driver1Mode, setDriver1Mode] = useState<AnalysisDriverType>("COLUMN");
  const [driver2Mode, setDriver2Mode] = useState<AnalysisDriverType>("PERCENTAGE");
  const [count1Column, setCount1Column] = useState<string>("");
  const [count2Column, setCount2Column] = useState<string | null>(null);
  const [shouldSkipSegmentationGroupedCountQuery, setShouldSkipSegmentationGroupedCountQuery] = useState<boolean>(true);
  const [shouldRefetchHeadroomData, setShouldRefetchHeadroomData] = useState<boolean>(false);
  const [customerColumn, setCustomerColumn] = useState<string | undefined>("");
  const [shouldRefetchDriverMetrics, setShouldRefetchDriverMetrics] = useState<boolean>(false);
  const [driver1Percentile, setDriver1Percentile] = useState<Percentile>(Percentile.P50);
  const [driver2Percentile, setDriver2Percentile] = useState<Percentile>(Percentile.P50);
  const [headroomDriver, setHeadroomDriver] = useState<"driver1" | "driver2" | null>(null);
  const [projectNameInput, setProjectNameInput] = useState<string>("");
  const [headroomDriverPercentile, setHeadroomDriverPercentile] = useState<number>(50);

  useEffect(() => {
    setSegmentGroups([{ index: 0, type: "", method: "DATA", segments: [], rangeColumn: "" }]);
    setTableName(null);
    setSelectedProject(undefined);
    setDriver1Type("");
    setDriver2Type("");
    setDriver1NumeratorType("");
    setDriver2NumeratorType("");
    setDriver1Mode("COLUMN");
    setDriver2Mode("PERCENTAGE");
    setCount1Column("");
    setCount2Column(null);
    setShouldSkipSegmentationGroupedCountQuery(true);
    setShouldRefetchHeadroomData(false);
    setCustomerColumn("");
    setShouldRefetchDriverMetrics(false);
    setDriver1Percentile(Percentile.P50);
    setDriver2Percentile(Percentile.P50);
    setHeadroomDriver(null);
    setProjectNameInput("");
    setHeadroomDriverPercentile(50);
  }, [selectedCube.cubeId]);

  const addSegmentGroup = useCallback(() => {
    setSegmentGroups((prev) => [
      ...prev,
      { index: prev.length, type: "", method: "DATA", segments: [], rangeColumn: "" },
    ]);
  }, []);

  const removeSegmentGroup = useCallback((index: number) => {
    setSegmentGroups((prev) => {
      const newGroups = prev
        .filter((group) => group.index !== index)
        .map((group, i) => ({
          ...group,
          index: i,
        }));
      return newGroups;
    });
  }, []);

  const updateSegmentGroupType = useCallback((index: number, type: string) => {
    setSegmentGroups((prev) =>
      prev.map((group) => (group.index === index ? { ...group, type, rangeColumn: group.rangeColumn || "" } : group))
    );
  }, []);

  const updateSegmentGroupMethod = useCallback((index: number, method: SegmentationMethod) => {
    setSegmentGroups((prev) =>
      prev.map((group) =>
        group.index === index ? { ...group, method, segments: method === "DATA" ? [] : group.segments } : group
      )
    );
  }, []);

  const updateSegmentGroupSegments = useCallback((index: number, segments: Segment[]) => {
    setSegmentGroups((prev) => prev.map((group) => (group.index === index ? { ...group, segments } : group)));
  }, []);

  const updateSegmentGroupRangeColumn = useCallback((index: number, rangeColumn: string) => {
    setSegmentGroups((prev) => prev.map((group) => (group.index === index ? { ...group, rangeColumn } : group)));
  }, []);

  const handleDriver1Change = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newType = event.target.value;
      setDriver1Type(newType);
    },
    [setDriver1Type]
  );

  const handleDriver2Change = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newType = event.target.value;
      setDriver2Type(newType);
    },
    [setDriver2Type]
  );

  const handleDriver1NumeratorChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setDriver1NumeratorType(event.target.value);
    },
    [setDriver1NumeratorType]
  );

  const handleDriver2NumeratorChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setDriver2NumeratorType(event.target.value);
    },
    [setDriver2NumeratorType]
  );

  const handleDriver1ModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMode = event.target.checked ? "PERCENTAGE" : "COLUMN";
      setDriver1Mode(newMode);
      if (newMode === "COLUMN") {
        setDriver1NumeratorType("");
      }
    },
    [setDriver1Mode, setDriver1NumeratorType]
  );

  const handleDriver2ModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMode = event.target.checked ? "PERCENTAGE" : "COLUMN";
      setDriver2Mode(newMode);
      if (newMode === "COLUMN") {
        setDriver2NumeratorType("");
      }
    },
    [setDriver2Mode, setDriver2NumeratorType]
  );

  return (
    <HeadroomContext.Provider
      value={{
        tableName,
        setTableName,
        segmentGroups,
        addSegmentGroup,
        removeSegmentGroup,
        updateSegmentGroupType,
        updateSegmentGroupMethod,
        updateSegmentGroupSegments,
        updateSegmentGroupRangeColumn,
        selectedProject,
        setSelectedProject,
        driver1Type,
        setDriver1Type,
        driver2Type,
        setDriver2Type,
        driver1NumeratorType,
        setDriver1NumeratorType,
        driver2NumeratorType,
        setDriver2NumeratorType,
        driver1Mode,
        setDriver1Mode,
        driver2Mode,
        setDriver2Mode,
        handleDriver1Change,
        handleDriver2Change,
        handleDriver1NumeratorChange,
        handleDriver2NumeratorChange,
        handleDriver1ModeChange,
        handleDriver2ModeChange,
        count1Column,
        setCount1Column,
        count2Column,
        setCount2Column,
        shouldSkipSegmentationGroupedCountQuery,
        setShouldSkipSegmentationGroupedCountQuery,
        customerColumn,
        setCustomerColumn,
        shouldRefetchDriverMetrics,
        setShouldRefetchDriverMetrics,
        driver1Percentile,
        setDriver1Percentile,
        driver2Percentile,
        setDriver2Percentile,
        headroomDriver,
        setHeadroomDriver,
        projectNameInput,
        setProjectNameInput,
        setSegmentGroups,
        shouldRefetchHeadroomData,
        setShouldRefetchHeadroomData,
        headroomDriverPercentile,
        setHeadroomDriverPercentile,
      }}
    >
      {children}
    </HeadroomContext.Provider>
  );
};

export const useHeadroomContext = () => {
  const context = useContext(HeadroomContext);
  if (!context) {
    throw new Error("useHeadroomContext must be used within a HeadroomProvider");
  }
  return context;
};
